/**
 * WireGuard-nøgler laves i browseren (beslutning 091).
 *
 * Den private nøgle forlader aldrig montørens enhed: den bruges kun til at bygge konfigurationen,
 * der vises som QR-kode eller hentes som fil, og gemmes ingen steder - hverken på serveren, i
 * localStorage eller i sessionStorage. Serveren får kun den offentlige nøgle.
 *
 * WireGuard bruger Curve25519 (X25519). Browserens egen WebCrypto kan lave nøglerne, så der er
 * ingen kryptobibliotek at holde opdateret.
 */

export interface WireGuardKeyPair {
  privateKey: string
  publicKey: string
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

function fromBase64Url(value: string): Uint8Array {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=')
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
}

export async function generateWireGuardKeyPair(): Promise<WireGuardKeyPair> {
  if (!globalThis.crypto?.subtle) {
    throw new Error('Browseren kan ikke lave nøgler her. Siden skal åbnes over https.')
  }

  let pair: CryptoKeyPair
  try {
    pair = (await crypto.subtle.generateKey({ name: 'X25519' }, true, ['deriveBits'])) as CryptoKeyPair
  } catch {
    throw new Error('Browseren kan ikke lave en WireGuard-nøgle. Opdatér browseren (Chrome, Edge, Safari eller Firefox).')
  }

  const publicKey = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey))
  const jwk = await crypto.subtle.exportKey('jwk', pair.privateKey)
  if (!jwk.d) throw new Error('Nøglen kunne ikke læses ud af browseren.')

  const privateKey = fromBase64Url(jwk.d)
  if (privateKey.length !== 32 || publicKey.length !== 32) {
    throw new Error('Browseren lavede en nøgle i et ukendt format.')
  }

  // Samme "clamping" som `wg genkey`. X25519 gør det alligevel ved brug, så den offentlige nøgle er den samme.
  privateKey[0] &= 248
  privateKey[31] = (privateKey[31] & 127) | 64

  return { privateKey: toBase64(privateKey), publicKey: toBase64(publicKey) }
}

/** Konfigurationen til WireGuard-appen. Indeholder den private nøgle - vis den kun for montøren selv. */
export function buildTechnicianConfig(
  privateKey: string,
  address: string,
  endpoint: { host: string; port: number; serverPublicKey: string; allowedIps: string }
): string {
  return [
    '[Interface]',
    `PrivateKey = ${privateKey}`,
    `Address = ${address}/32`,
    '',
    '[Peer]',
    `PublicKey = ${endpoint.serverPublicKey}`,
    `Endpoint = ${endpoint.host}:${endpoint.port}`,
    `AllowedIPs = ${endpoint.allowedIps}`,
    'PersistentKeepalive = 25',
    ''
  ].join('\n')
}

/**
 * Routerens konfigurationsfil (beslutning 091). Laves i Superadmins browser sammen med en ny nøgle,
 * og kan hentes ÉN gang - bagefter er den private nøgle glemt, og en ny fil kræver en ny nøgle.
 *
 * AllowedIPs kommer fra gatewayen og SKAL indeholde montørnettet, ellers kasserer routeren trafikken
 * fra en montør med serviceadgang.
 */
export function buildRouterConfig(
  privateKey: string,
  site: { name: string; vpnAddress: string },
  endpoint: { host: string; port: number; serverPublicKey: string; allowedIps: string }
): string {
  return [
    `# ${site.name} - WireGuard til ${endpoint.host}`,
    '# Indeholder routerens PRIVATE nøgle. Læg den i routeren og slet filen bagefter.',
    '[Interface]',
    `PrivateKey = ${privateKey}`,
    `Address = ${site.vpnAddress}/32`,
    '',
    '[Peer]',
    `PublicKey = ${endpoint.serverPublicKey}`,
    `Endpoint = ${endpoint.host}:${endpoint.port}`,
    `AllowedIPs = ${endpoint.allowedIps}`,
    'PersistentKeepalive = 25',
    ''
  ].join('\n')
}

/**
 * Samme router-opsætning som CLI-kommandoer til en Ubiquiti EdgeRouter (EdgeOS, fx ER-6P) - kopieres ind efter
 * `configure`. Laves og hentes på samme måde som routerfilen: ÉN gang, og den private nøgle står i teksten.
 *
 * Kun tunnelen. Kundens LAN (alias, montøradresse, firewall) sættes op bagefter under "LAN-adgang og EdgeRouter".
 */
export function buildEdgeOsRouterCommands(
  privateKey: string,
  site: { name: string; vpnAddress: string },
  endpoint: { host: string; port: number; serverPublicKey: string; allowedIps: string }
): string {
  const peer = `set interfaces wireguard wg0 peer '${endpoint.serverPublicKey}'`
  const allowed = endpoint.allowedIps
    .split(',')
    .map((ip) => ip.trim())
    .filter(Boolean)
  return [
    `# ${site.name} - EdgeRouter (EdgeOS) til ${endpoint.host}`,
    '# Indeholder routerens PRIVATE nøgle. Indsæt i routeren og slet filen bagefter.',
    '# Log ind på routeren med SSH, skriv "configure", og indsæt linjerne herunder.',
    '',
    'configure',
    '',
    `set interfaces wireguard wg0 address '${site.vpnAddress}/32'`,
    "set interfaces wireguard wg0 description 'TC VPN'",
    'set interfaces wireguard wg0 route-allowed-ips true',
    `set interfaces wireguard wg0 private-key '${privateKey}'`,
    ...allowed.map((ip) => `${peer} allowed-ips '${ip}'`),
    `${peer} endpoint '${endpoint.host}:${endpoint.port}'`,
    `${peer} persistent-keepalive '25'`,
    '',
    'show interfaces wireguard wg0',
    '',
    'commit',
    'save',
    'exit',
    '',
    '# LAN-adgang for montøren: Projekt Overblik -> Administration -> VPN -> "LAN-adgang og EdgeRouter".',
    ''
  ].join('\n')
}

export function edgeOsCommandsFileName(siteId: number): string {
  return `tcvpn-site${siteId}-edgerouter.txt`
}

/** Filnavn til routeren. Kort, fordi mange routere bruger det som interfacenavn (maks. 15 tegn). */
export function routerConfigFileName(siteId: number): string {
  return `tcvpn-site${siteId}.conf`
}

/** Gemmer tekst som fil hos brugeren. */
export function downloadTextFile(fileName: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}
